// src/store/actions/faqActions.js
import { FAQ_ACTIONS } from './actionTypes';
import faqService from '../../services/faqService';

// Fetch FAQs
export const fetchFaqs = (params = {}) => async (dispatch) => {
  try {
    dispatch({ type: FAQ_ACTIONS.FETCH_FAQS_REQUEST });

    const response = await faqService.getFaqs(params);
    
    dispatch({
      type: FAQ_ACTIONS.FETCH_FAQS_SUCCESS,
      payload: response,
    });
  } catch (error) {
    dispatch({
      type: FAQ_ACTIONS.FETCH_FAQS_FAILURE,
      payload: error.message || 'Failed to fetch FAQs',
    });
  }
};

// Fetch single FAQ
export const fetchFaq = (id) => async (dispatch) => {
  try {
    dispatch({ type: FAQ_ACTIONS.FETCH_FAQ_REQUEST });

    const response = await faqService.getFaqById(id);
    
    dispatch({
      type: FAQ_ACTIONS.FETCH_FAQ_SUCCESS,
      payload: response.data,
    });
    
    return response.data;
  } catch (error) {
    dispatch({
      type: FAQ_ACTIONS.FETCH_FAQ_FAILURE,
      payload: error.message || 'Failed to fetch FAQ',
    });
    throw error;
  }
};

// Create FAQ
export const createFaq = (faqData) => async (dispatch) => {
  try {
    dispatch({ type: FAQ_ACTIONS.CREATE_FAQ_REQUEST });

    const response = await faqService.createFaq(faqData);
    
    dispatch({
      type: FAQ_ACTIONS.CREATE_FAQ_SUCCESS,
      payload: response.data,
    });
    
    return response.data;
  } catch (error) {
    dispatch({
      type: FAQ_ACTIONS.CREATE_FAQ_FAILURE,
      payload: error.message || 'Failed to create FAQ',
    });
    throw error;
  }
};

// Update FAQ
export const updateFaq = (id, faqData) => async (dispatch) => {
  try {
    dispatch({ type: FAQ_ACTIONS.UPDATE_FAQ_REQUEST });

    const response = await faqService.updateFaq(id, faqData);
    
    dispatch({
      type: FAQ_ACTIONS.UPDATE_FAQ_SUCCESS,
      payload: response.data,
    });
    
    return response.data;
  } catch (error) {
    dispatch({
      type: FAQ_ACTIONS.UPDATE_FAQ_FAILURE,
      payload: error.message || 'Failed to update FAQ',
    });
    throw error;
  }
};

// Delete FAQ
export const deleteFaq = (id) => async (dispatch) => {
  try {
    dispatch({ type: FAQ_ACTIONS.DELETE_FAQ_REQUEST });

    const response = await faqService.deleteFaq(id);
    
    dispatch({
      type: FAQ_ACTIONS.DELETE_FAQ_SUCCESS,
      payload: { id, message: response.message },
    });
    
    return response;
  } catch (error) {
    dispatch({
      type: FAQ_ACTIONS.DELETE_FAQ_FAILURE,
      payload: error.message || 'Failed to delete FAQ',
    });
    throw error;
  }
};

// Update FAQ status
export const updateFaqStatus = (id, status) => async (dispatch) => {
  try {
    dispatch({ type: FAQ_ACTIONS.UPDATE_FAQ_STATUS_REQUEST });

    const response = await faqService.updateFaqStatus(id, status);
    
    dispatch({
      type: FAQ_ACTIONS.UPDATE_FAQ_STATUS_SUCCESS,
      payload: response.data,
    });
    
    return response.data;
  } catch (error) {
    dispatch({
      type: FAQ_ACTIONS.UPDATE_FAQ_STATUS_FAILURE,
      payload: error.message || 'Failed to update FAQ status',
    });
    throw error;
  }
};

// Fetch FAQ feedback
export const fetchFaqFeedback = (faqId, params = {}) => async (dispatch) => {
  try {
    dispatch({ type: FAQ_ACTIONS.FETCH_FAQ_FEEDBACK_REQUEST });

    const response = await faqService.getFaqFeedback(faqId, params);
    
    dispatch({
      type: FAQ_ACTIONS.FETCH_FAQ_FEEDBACK_SUCCESS,
      payload: { faqId, ...response },
    });
  } catch (error) {
    dispatch({
      type: FAQ_ACTIONS.FETCH_FAQ_FEEDBACK_FAILURE,
      payload: error.message || 'Failed to fetch feedback',
    });
  }
};

// Fetch related FAQs
export const fetchRelatedFaqs = (faqId, params = {}) => async (dispatch) => {
  try {
    dispatch({ type: FAQ_ACTIONS.FETCH_RELATED_FAQS_REQUEST });

    const response = await faqService.getRelatedFaqs(faqId, params);
    
    dispatch({
      type: FAQ_ACTIONS.FETCH_RELATED_FAQS_SUCCESS,
      payload: { faqId, data: response.data },
    });
  } catch (error) {
    dispatch({
      type: FAQ_ACTIONS.FETCH_RELATED_FAQS_FAILURE,
      payload: error.message || 'Failed to fetch related FAQs',
    });
  }
};

// Add related FAQ
export const addRelatedFaq = (faqId, relatedFaqId, relationStrength = 1) => async (dispatch) => {
  try {
    dispatch({ type: FAQ_ACTIONS.ADD_RELATED_FAQ_REQUEST });

    const response = await faqService.addRelatedFaq(faqId, relatedFaqId, relationStrength);
    
    dispatch({
      type: FAQ_ACTIONS.ADD_RELATED_FAQ_SUCCESS,
      payload: response.data,
    });
    
    return response.data;
  } catch (error) {
    dispatch({
      type: FAQ_ACTIONS.ADD_RELATED_FAQ_FAILURE,
      payload: error.message || 'Failed to add related FAQ',
    });
    throw error;
  }
};

// Remove related FAQ
export const removeRelatedFaq = (faqId, relatedFaqId) => async (dispatch) => {
  try {
    dispatch({ type: FAQ_ACTIONS.REMOVE_RELATED_FAQ_REQUEST });

    const response = await faqService.removeRelatedFaq(faqId, relatedFaqId);
    
    dispatch({
      type: FAQ_ACTIONS.REMOVE_RELATED_FAQ_SUCCESS,
      payload: { faqId, relatedFaqId, message: response.message },
    });
    
    return response;
  } catch (error) {
    dispatch({
      type: FAQ_ACTIONS.REMOVE_RELATED_FAQ_FAILURE,
      payload: error.message || 'Failed to remove related FAQ',
    });
    throw error;
  }
};

// Upload FAQ image
export const uploadFaqImage = (faqId, file) => async (dispatch) => {
  try {
    dispatch({ type: FAQ_ACTIONS.UPLOAD_FAQ_IMAGE_REQUEST });

    const response = await faqService.uploadFaqImage(faqId, file);
    
    dispatch({
      type: FAQ_ACTIONS.UPLOAD_FAQ_IMAGE_SUCCESS,
      payload: response.data,
    });
    
    return response.data;
  } catch (error) {
    dispatch({
      type: FAQ_ACTIONS.UPLOAD_FAQ_IMAGE_FAILURE,
      payload: error.message || 'Failed to upload image',
    });
    throw error;
  }
};

// Delete FAQ image
export const deleteFaqImage = (faqId, imageId) => async (dispatch) => {
  try {
    dispatch({ type: FAQ_ACTIONS.DELETE_FAQ_IMAGE_REQUEST });

    const response = await faqService.deleteFaqImage(faqId, imageId);
    
    dispatch({
      type: FAQ_ACTIONS.DELETE_FAQ_IMAGE_SUCCESS,
      payload: { imageId, message: response.message },
    });
    
    return response;
  } catch (error) {
    dispatch({
      type: FAQ_ACTIONS.DELETE_FAQ_IMAGE_FAILURE,
      payload: error.message || 'Failed to delete image',
    });
    throw error;
  }
};

// Fetch FAQ categories
export const fetchFaqCategories = (params = {}) => async (dispatch) => {
  try {
    dispatch({ type: FAQ_ACTIONS.FETCH_FAQ_CATEGORIES_REQUEST });

    const response = await faqService.getFaqCategories(params);
    
    dispatch({
      type: FAQ_ACTIONS.FETCH_FAQ_CATEGORIES_SUCCESS,
      payload: response,
    });
  } catch (error) {
    dispatch({
      type: FAQ_ACTIONS.FETCH_FAQ_CATEGORIES_FAILURE,
      payload: error.message || 'Failed to fetch categories',
    });
  }
};

// Fetch FAQ tags
export const fetchFaqTags = (params = {}) => async (dispatch) => {
  try {
    dispatch({ type: FAQ_ACTIONS.FETCH_FAQ_TAGS_REQUEST });

    const response = await faqService.getFaqTags(params);
    
    dispatch({
      type: FAQ_ACTIONS.FETCH_FAQ_TAGS_SUCCESS,
      payload: response,
    });
  } catch (error) {
    dispatch({
      type: FAQ_ACTIONS.FETCH_FAQ_TAGS_FAILURE,
      payload: error.message || 'Failed to fetch tags',
    });
  }
};

// Bulk delete FAQs
export const bulkDeleteFaqs = (ids) => async (dispatch) => {
  try {
    dispatch({ type: FAQ_ACTIONS.BULK_DELETE_FAQS_REQUEST });

    const response = await faqService.bulkDeleteFaqs(ids);
    
    dispatch({
      type: FAQ_ACTIONS.BULK_DELETE_FAQS_SUCCESS,
      payload: response,
    });
    
    return response;
  } catch (error) {
    dispatch({
      type: FAQ_ACTIONS.BULK_DELETE_FAQS_FAILURE,
      payload: error.message || 'Failed to bulk delete FAQs',
    });
    throw error;
  }
};

// Delete FAQ document
export const deleteFaqDocument = (faqId, documentId) => async (dispatch) => {
  try {
    dispatch({ type: FAQ_ACTIONS.DELETE_DOCUMENT_REQUEST });

    const response = await faqService.deleteDocument(faqId, documentId);
    
    dispatch({
      type: FAQ_ACTIONS.DELETE_DOCUMENT_SUCCESS,
      payload: { documentId, message: response.message },
    });
    
    return response;
  } catch (error) {
    dispatch({
      type: FAQ_ACTIONS.DELETE_DOCUMENT_FAILURE,
      payload: error.message || 'Failed to delete document',
    });
    throw error;
  }
};

// Fetch FAQ statistics
export const fetchFaqStats = () => async (dispatch) => {
  try {
    dispatch({ type: FAQ_ACTIONS.FETCH_FAQ_STATS_REQUEST });

    const response = await faqService.getFaqStats();
    
    dispatch({
      type: FAQ_ACTIONS.FETCH_FAQ_STATS_SUCCESS,
      payload: response.data,
    });
  } catch (error) {
    dispatch({
      type: FAQ_ACTIONS.FETCH_FAQ_STATS_FAILURE,
      payload: error.message || 'Failed to fetch FAQ stats',
    });
  }
};

// Record view
export const recordFaqView = (faqId) => async (dispatch) => {
  try {
    await faqService.recordView(faqId);
  } catch (error) {
    console.error('Failed to record view:', error);
  }
};

// Record feedback
export const recordFaqFeedback = (faqId, feedbackData) => async (dispatch) => {
  try {
    const response = await faqService.recordFeedback(faqId, feedbackData);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Set FAQ filters
export const setFaqFilters = (filters) => ({
  type: FAQ_ACTIONS.SET_FAQ_FILTERS,
  payload: filters,
});

// Clear error
export const clearFaqError = () => ({
  type: FAQ_ACTIONS.CLEAR_FAQ_ERROR,
});

// Clear success
export const clearFaqSuccess = () => ({
  type: FAQ_ACTIONS.CLEAR_FAQ_SUCCESS,
});

// Clear selected FAQ
export const clearSelectedFaq = () => ({
  type: FAQ_ACTIONS.CLEAR_SELECTED_FAQ,
});